export type Plan = "free" | "pro";

export interface CertInfo {
  subject: string;
  issuer: string;
  not_after: string;
  not_before: string;
  dns_names: string[];
  days_remaining: number;
  tls_version: string;
  cipher_suite: string;
}

export interface ProbeResult {
  region: string;
  http_status: number | null;
  total_ms: number | null;
  dns_ms: number | null;
  tls_ms: number | null;
  ttfb_ms: number | null;
  error: string | null;
  response_headers: Record<string, string | null> | null;
  body_bytes: number | null;
  redirected: boolean;
  final_url: string | null;
  resolved_ip: string | null;
  cert_info: CertInfo | null;
  cache_status: string | null;
  cache_provider: string | null;
  cache_age_secs: number | null;
}

export type CacheStatus = "HIT" | "MISS" | "BYPASS" | "STALE" | "EXPIRED" | "DYNAMIC" | "UNKNOWN";

export interface CacheRegionStats {
  hit: number;
  miss: number;
  bypass: number;
  other: number;
  hit_rate: number;
}

export interface MonitorCacheStats {
  regions: Record<string, CacheRegionStats>;
  provider: string;
  window: string;
}

export interface Percentiles {
  p50: number | null;
  p95: number | null;
  p99: number | null;
}

export type ProbeStatus = "pending" | "running" | "complete" | "error";

export interface ProbeRun {
  run_id: string;
  status: ProbeStatus;
  url: string;
  method: string;
  region_count: number;
  regions_done: number;
  created_at: string;
  completed_at: string | null;
  percentiles: Percentiles;
  results: ProbeResult[];
}

export interface Monitor {
  id: string;
  name: string;
  url: string;
  method: string;
  interval_minutes: number;
  regions: string[];
  is_active: boolean;
  alert_latency_ms: number | null;
  alert_cert_expiry_days: number | null;
  alert_email: string | null;
  alert_webhook_url: string | null;
  next_run_at: string;
  created_at: string;
  group_id: string | null;
  // TCP-specific fields; zero/null for HTTP monitors.
  tcp_tls: boolean;
  tcp_skip_verify: boolean;
  tcp_send_string: string | null;
  tcp_expect_string: string | null;
  tcp_timeout_secs: number;
}

export interface MonitorGroup {
  id: string;
  name: string;
  description: string | null;
  color: string;
  created_at: string;
}

export interface MonitorRun {
  id: string;
  status: ProbeStatus;
  region_count: number;
  regions_done: number;
  created_at: string;
  completed_at: string | null;
  p50: number | null;
  p95: number | null;
  p99: number | null;
}

export interface MonitorIncident {
  run_id: string;
  created_at: string;
  completed_at: string | null;
  status: ProbeStatus;
  region_count: number;
  regions_done: number;
  error_count: number;
  p50: number | null;
  p95: number | null;
  p99: number | null;
  is_error: boolean;
  is_high_latency: boolean;
}

export interface StatusPageMonitorEntry {
  monitor_id: string;
  monitor_name: string;
  display_name: string | null;
  display_order: number;
}

export interface StatusPage {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  is_public: boolean;
  created_at: string;
  monitors: StatusPageMonitorEntry[];
}

export type MonitorCurrentStatus = "operational" | "degraded" | "down" | "unknown";

export interface PublicMonitor {
  name: string;
  current_status: MonitorCurrentStatus;
  uptime_pct: number;
  days: Array<{
    date: string;
    status: "up" | "down" | "degraded" | "no-data";
    up_count: number;
    down_count: number;
  }>;
}

export interface PublicStatusPage {
  slug: string;
  title: string;
  description: string | null;
  overall_status: MonitorCurrentStatus;
  monitors: PublicMonitor[];
}

export const REGIONS: Record<string, string> = {
  "us-east": "N. America East",
  "us-west": "N. America West",
  "eu-west": "Europe West",
  "eu-central": "Europe Central",
  "ap-southeast": "Asia Pacific SE",
  "ap-northeast": "Asia Pacific NE",
  "ap-australia": "Australia",
  "sa-east": "South America",
};

export const ALL_REGIONS = Object.keys(REGIONS);

export type IntervalMinutes = 1 | 5 | 10 | 15 | 30 | 60;
